import React from 'react';
import './LegalPage.css';

export default function TermsPage() {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <h1>Terms of Service</h1>
        <p className="legal-company"><strong>ChronoTech Robotics LLC</strong></p>
        <p className="legal-effective"><em>Effective Date: August 23, 2025</em></p>
        <p className="legal-last-updated"><em>Last Updated: August 23, 2025</em></p>
      </div>

      <div className="legal-intro">
        <p>
          Welcome to ChronoTech Robotics LLC ("Company," "we," "our," or "us"). These Terms of Service ("Terms") 
          govern your use of our website, services, educational programs, and any related applications or platforms 
          (collectively, the "Services"). By accessing or using our Services, you agree to be legally bound by these 
          Terms. If you do not agree to these Terms, you must not access or use our Services.
        </p>
      </div>

      <div className="legal-toc">
        <h2>Table of Contents</h2>
        <ul>
          <li><a href="#acceptance">1. Acceptance of Terms</a></li>
          <li><a href="#services">2. Description of Services</a></li>
          <li><a href="#eligibility">3. Eligibility and User Requirements</a></li>
          <li><a href="#registration">4. Account Registration and Security</a></li>
          <li><a href="#programs">5. Educational Programs and Participation</a></li>
          <li><a href="#payment">6. Payment Terms and Refund Policy</a></li>
          <li><a href="#shipping">7. Shipping and Delivery</a></li>
          <li><a href="#conduct">8. User Conduct and Acceptable Use</a></li>
          <li><a href="#content">9. User-Generated Content</a></li>
          <li><a href="#ip">10. Intellectual Property Rights</a></li>
          <li><a href="#privacy">11. Privacy and Data Protection</a></li>
          <li><a href="#safety">12. Safety and Supervision Requirements</a></li>
          <li><a href="#liability">13. Limitation of Liability</a></li>
          <li><a href="#indemnification">14. Indemnification</a></li>
          <li><a href="#termination">15. Termination and Suspension</a></li>
          <li><a href="#disputes">16. Dispute Resolution and Arbitration</a></li>
          <li><a href="#changes">17. Modifications to Terms</a></li>
          <li><a href="#general">18. General Provisions</a></li>
          <li><a href="#contact">19. Contact Information</a></li>
        </ul>
      </div>

      <section id="acceptance" className="legal-section">
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing our website at chronotechrobotics.com, registering for any program, purchasing products or 
          services, or otherwise using our Services, you acknowledge that you have read, understood, and agree to 
          be bound by these Terms of Service and our Privacy Policy, which is incorporated herein by reference.
        </p>
        <p>
          These Terms constitute a legally binding agreement between you and ChronoTech Robotics LLC. If you are 
          using our Services on behalf of a minor child, you represent and warrant that you are the parent or legal 
          guardian of such child and have the authority to agree to these Terms on their behalf.
        </p>
      </section>

      <section id="services" className="legal-section">
        <h2>2. Description of Services</h2>
        <p>
          ChronoTech Robotics LLC provides educational robotics programs and services, including but not limited to:
        </p>
        <ul>
          <li><strong>Robo Kids Learning:</strong> Virtual, hands-on robotics education programs for children and families</li>
          <li><strong>ChronoTech Innovator Program:</strong> Advanced robotics and entrepreneurship training for students</li>
          <li><strong>Educational Kits:</strong> Robotics components, sensors, and learning materials</li>
          <li><strong>Online Platform:</strong> Web-based learning management system and resources</li>
          <li><strong>Virtual Classes:</strong> Live instruction via video conferencing platforms</li>
          <li><strong>Support Services:</strong> Technical assistance, educational guidance, and customer support</li>
        </ul>
        <p>
          We reserve the right to modify, suspend, or discontinue any aspect of our Services at any time without 
          prior notice, except as required by applicable law.
        </p>
      </section>

      <section id="eligibility" className="legal-section">
        <h2>3. Eligibility and User Requirements</h2>
        <h3>3.1 Age Requirements</h3>
        <p>
          To register for our Services, you must be at least 18 years of age or the age of majority in your 
          jurisdiction. If you are registering a minor child for our programs, you must be their parent or legal guardian.
        </p>
        
        <h3>3.2 Capacity and Authority</h3>
        <p>You represent and warrant that you:</p>
        <ul>
          <li>Have the legal capacity to enter into binding agreements</li>
          <li>Are not prohibited from using our Services under applicable law</li>
          <li>Will provide accurate and complete information during registration</li>
          <li>Will maintain the confidentiality and security of your account credentials</li>
        </ul>

        <h3>3.3 Prohibited Users</h3>
        <p>Our Services are not intended for and may not be used by:</p>
        <ul>
          <li>Individuals or entities attempting to reverse-engineer or exploit our curriculum</li>
          <li>Competitors seeking to copy our methodologies or business practices</li>
          <li>Users providing false or misleading registration information</li>
          <li>Anyone previously banned or suspended from our Services</li>
        </ul>
      </section>

      <section id="registration" className="legal-section">
        <h2>4. Account Registration and Security</h2>
        <h3>4.1 Account Creation</h3>
        <p>
          To access certain features of our Services, you must create an account by providing accurate and complete 
          information as prompted by our registration form. You are responsible for maintaining the accuracy of your 
          account information and promptly updating it when changes occur.
        </p>

        <h3>4.2 Account Security</h3>
        <p>
          You are solely responsible for maintaining the confidentiality of your account credentials and for all 
          activities that occur under your account. You must immediately notify us of any unauthorized use of your 
          account or any other breach of security.
        </p>

        <h3>4.3 Multiple Accounts</h3>
        <p>
          You may not create multiple accounts to circumvent restrictions, obtain additional benefits, or for any 
          other purpose without our express written permission.
        </p>
      </section>

      <section id="programs" className="legal-section">
        <h2>5. Educational Programs and Participation</h2>
        <h3>5.1 Program Structure</h3>
        <p>
          Our educational programs are designed to provide hands-on learning experiences in robotics, engineering, 
          and related STEM fields. Programs may include live virtual sessions, self-paced learning modules, 
          educational kits, and supplementary materials.
        </p>

        <h3>5.2 Participation Requirements</h3>
        <p>Program participants must:</p>
        <ul>
          <li>Attend scheduled sessions or complete modules within specified timeframes</li>
          <li>Maintain appropriate behavior during live sessions</li>
          <li>Use educational materials solely for learning purposes</li>
          <li>Ensure adult supervision for child participants as required</li>
          <li>Have access to necessary technology and internet connectivity</li>
        </ul>

        <h3>5.3 Recording and Documentation</h3>
        <p>
          Live sessions may be recorded for quality assurance, educational purposes, and to provide make-up content 
          for enrolled participants. By participating in live sessions, you consent to such recording and potential 
          distribution to other enrolled families.
        </p>
      </section>

      <section id="payment" className="legal-section">
        <h2>6. Payment Terms and Refund Policy</h2>
        <h3>6.1 Payment Processing</h3>
        <p>
          All payments are processed securely through third-party payment processors. By providing payment information, 
          you authorize us to charge the applicable fees for your selected services. Prices are subject to change 
          with reasonable notice.
        </p>

        <h3>6.2 Refund Policy</h3>
        <p>
          <strong>All sales are final.</strong> We do not offer refunds for any reason, including but not limited to:
        </p>
        <ul>
          <li>Change of mind or circumstances</li>
          <li>Inability to attend scheduled sessions</li>
          <li>Technical difficulties on the user's end</li>
          <li>Dissatisfaction with program content or instruction</li>
          <li>Shipping delays or delivery issues</li>
        </ul>

        <h3>6.3 Promotional Pricing</h3>
        <p>
          Promotional offers, discounts, and special pricing are subject to specific terms and conditions. 
          Promotional pricing cannot be combined with other offers unless explicitly stated.
        </p>
      </section>

      <section id="shipping" className="legal-section">
        <h2>7. Shipping and Delivery</h2>
        <h3>7.1 Shipping Policy</h3>
        <p>
          Educational kits and materials are shipped to the address provided during registration prior to program 
          commencement. You are responsible for providing accurate shipping information and ensuring someone is 
          available to receive deliveries.
        </p>

        <h3>7.2 Risk of Loss</h3>
        <p>
          Risk of loss and title for physical items pass to you upon delivery to the shipping address. While we 
          will work in good faith to assist with shipping issues, we are not responsible for:
        </p>
        <ul>
          <li>Delays caused by shipping carriers</li>
          <li>Lost or stolen packages after delivery</li>
          <li>Damage occurring during shipping</li>
          <li>Customs fees, duties, or taxes for international shipments</li>
        </ul>

        <h3>7.3 International Shipping</h3>
        <p>
          International shipping may be subject to additional fees, customs requirements, and extended delivery 
          times. International customers are responsible for compliance with their local import regulations.
        </p>
      </section>

      <section id="conduct" className="legal-section">
        <h2>8. User Conduct and Acceptable Use</h2>
        <h3>8.1 Prohibited Activities</h3>
        <p>You agree not to engage in any of the following prohibited activities:</p>
        <ul>
          <li>Using our Services for any unlawful purpose or in violation of applicable laws</li>
          <li>Harassing, threatening, or intimidating other users or staff members</li>
          <li>Attempting to gain unauthorized access to our systems or other users' accounts</li>
          <li>Distributing malware, viruses, or other harmful code</li>
          <li>Sharing copyrighted materials without proper authorization</li>
          <li>Impersonating any person or entity or falsely representing your affiliation</li>
          <li>Interfering with or disrupting the operation of our Services</li>
          <li>Using automated systems to access our Services without permission</li>
        </ul>

        <h3>8.2 Content Standards</h3>
        <p>
          Any content you submit, post, or share through our Services must be appropriate for an educational 
          environment and must not contain offensive, discriminatory, or harmful material.
        </p>
      </section>

      <section id="content" className="legal-section">
        <h2>9. User-Generated Content</h2>
        <h3>9.1 Ownership and License</h3>
        <p>
          You retain ownership of any content you create or submit through our Services. However, by submitting 
          content, you grant us a non-exclusive, worldwide, royalty-free license to use, display, and distribute 
          such content in connection with our Services and for educational purposes.
        </p>

        <h3>9.2 Content Responsibility</h3>
        <p>
          You are solely responsible for any content you submit and must ensure you have all necessary rights 
          and permissions. We reserve the right to remove any content that violates these Terms or our community standards.
        </p>
      </section>

      <section id="ip" className="legal-section">
        <h2>10. Intellectual Property Rights</h2>
        <h3>10.1 Company Property</h3>
        <p>
          All materials, content, software, designs, curricula, methodologies, trademarks, and other intellectual 
          property provided through our Services are owned by or licensed to ChronoTech Robotics LLC and are 
          protected by copyright, trademark, and other intellectual property laws.
        </p>

        <h3>10.2 Limited License</h3>
        <p>
          We grant you a limited, non-exclusive, non-transferable license to access and use our Services and 
          educational materials solely for your personal, non-commercial educational purposes in accordance with these Terms.
        </p>

        <h3>10.3 Restrictions</h3>
        <p>You may not:</p>
        <ul>
          <li>Copy, reproduce, or distribute our materials without written permission</li>
          <li>Create derivative works based on our content</li>
          <li>Use our trademarks or branding without authorization</li>
          <li>Reverse engineer or attempt to extract source code from our software</li>
          <li>Use our content for commercial purposes</li>
        </ul>
      </section>

      <section id="privacy" className="legal-section">
        <h2>11. Privacy and Data Protection</h2>
        <p>
          Your privacy is important to us. Our collection, use, and disclosure of personal information is governed 
          by our Privacy Policy, which is incorporated into these Terms by reference. By using our Services, you 
          consent to our privacy practices as described in our Privacy Policy.
        </p>
        <p>
          We implement appropriate security measures to protect your personal information, but you acknowledge that 
          no method of transmission over the internet is 100% secure.
        </p>
      </section>

      <section id="safety" className="legal-section">
        <h2>12. Safety and Supervision Requirements</h2>
        <h3>12.1 Adult Supervision</h3>
        <p>
          All activities involving children must be supervised by a responsible adult at all times. Parents and 
          guardians are solely responsible for ensuring the safety of child participants during all program activities.
        </p>

        <h3>12.2 Safety Guidelines</h3>
        <p>Participants must:</p>
        <ul>
          <li>Follow all safety instructions provided by instructors</li>
          <li>Use educational kits and materials as intended</li>
          <li>Maintain a safe learning environment</li>
          <li>Report any safety concerns immediately</li>
        </ul>

        <h3>12.3 Medical Considerations</h3>
        <p>
          You are responsible for determining whether program activities are appropriate given any medical conditions, 
          allergies, or physical limitations of participants.
        </p>
      </section>

      <section id="liability" className="legal-section">
        <h2>13. Limitation of Liability</h2>
        <h3>13.1 Disclaimer of Warranties</h3>
        <p>
          OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR 
          IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS 
          FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>

        <h3>13.2 Limitation of Damages</h3>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL CHRONOTECH ROBOTICS LLC BE LIABLE FOR ANY 
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS 
          OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATING TO YOUR USE OF OUR SERVICES.
        </p>

        <h3>13.3 Maximum Liability</h3>
        <p>
          OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR OUR SERVICES SHALL NOT 
          EXCEED THE TOTAL AMOUNT PAID BY YOU FOR OUR SERVICES DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT 
          GIVING RISE TO LIABILITY.
        </p>
      </section>

      <section id="indemnification" className="legal-section">
        <h2>14. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless ChronoTech Robotics LLC, its officers, directors, 
          employees, agents, and affiliates from and against any and all claims, damages, losses, costs, and 
          expenses (including reasonable attorneys' fees) arising out of or relating to:
        </p>
        <ul>
          <li>Your use of our Services</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any applicable law or regulation</li>
          <li>Any injury or damage arising from your participation in our programs</li>
          <li>Any content you submit or share through our Services</li>
        </ul>
      </section>

      <section id="termination" className="legal-section">
        <h2>15. Termination and Suspension</h2>
        <h3>15.1 Termination by You</h3>
        <p>
          You may terminate your account at any time by contacting us. Termination does not entitle you to any 
          refund of fees paid.
        </p>

        <h3>15.2 Termination by Us</h3>
        <p>
          We may suspend or terminate your access to our Services immediately and without notice if you violate 
          these Terms or engage in conduct that we determine, in our sole discretion, is harmful to our business, 
          other users, or third parties.
        </p>

        <h3>15.3 Effect of Termination</h3>
        <p>
          Upon termination, your right to use our Services will cease immediately, and you must discontinue all 
          use of our materials and return or destroy any confidential information.
        </p>
      </section>

      <section id="disputes" className="legal-section">
        <h2>16. Dispute Resolution and Arbitration</h2>
        <h3>16.1 Informal Resolution</h3>
        <p>
          Before initiating any formal dispute resolution process, you agree to attempt to resolve any dispute 
          through direct communication with us by contacting our support team.
        </p>

        <h3>16.2 Binding Arbitration</h3>
        <p>
          Any dispute arising out of or relating to these Terms or our Services shall be resolved through binding 
          arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration Association. 
          The arbitration shall take place in [Your State/Location] and be conducted in English.
        </p>

        <h3>16.3 Class Action Waiver</h3>
        <p>
          You agree that any arbitration shall be conducted on an individual basis and not as part of a class, 
          collective, or representative action.
        </p>
      </section>

      <section id="changes" className="legal-section">
        <h2>17. Modifications to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will notify users of material changes by 
          posting the updated Terms on our website with a new effective date. Your continued use of our Services 
          after the posting of revised Terms constitutes your acceptance of such changes.
        </p>
        <p>
          We encourage you to review these Terms periodically to stay informed of any updates.
        </p>
      </section>

      <section id="general" className="legal-section">
        <h2>18. General Provisions</h2>
        <h3>18.1 Governing Law</h3>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of [Your State], without 
          regard to its conflict of law principles.
        </p>

        <h3>18.2 Severability</h3>
        <p>
          If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall 
          remain in full force and effect.
        </p>

        <h3>18.3 Entire Agreement</h3>
        <p>
          These Terms, together with our Privacy Policy and any other policies referenced herein, constitute the 
          entire agreement between you and ChronoTech Robotics LLC regarding your use of our Services.
        </p>

        <h3>18.4 Assignment</h3>
        <p>
          You may not assign or transfer your rights or obligations under these Terms without our prior written 
          consent. We may assign our rights and obligations without restriction.
        </p>

        <h3>18.5 Force Majeure</h3>
        <p>
          We shall not be liable for any failure or delay in performance under these Terms due to circumstances 
          beyond our reasonable control, including but not limited to acts of God, natural disasters, war, 
          terrorism, or government actions.
        </p>
      </section>

      <section id="contact" className="legal-section">
        <h2>19. Contact Information</h2>
        <p>
          If you have any questions about these Terms of Service, please contact us at:
        </p>
        <div className="contact-info">
          <p><strong>ChronoTech Robotics LLC</strong></p>
          <p>Email: support@chronotechrobotics.com</p>
          <p>Website: chronotechrobotics.com</p>
          
         
        </div>
      </section>

      <div className="legal-footer">
        <p>
          <em>This document was last updated on August 23, 2025. Please check our website regularly for updates 
          to these Terms of Service.</em>
        </p>
      </div>
    </div>
  );
}